import { Box, Typography, useTheme, useAutocomplete } from "@mui/material";
import ErrorDisplay from "lib/components/error/ErrorDisplay/ErrorDisplay";
import SimpleAutocomplete from "lib/components/formInputs/SimpleAutocomplete/SimpleAutocomplete";
import Page from "lib/components/layout/Page/Page";
import ConfirmationModal from "lib/components/modals/ConfirmationModal/ConfirmationModal";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { ACTIONS, PATHS } from "lib/routing";
import { mobileNavigate } from "lib/routing/navigate/navigate";
import { EVENTS } from "lib/util/GoogleAnalyticsUtil/events";
import { logEventToGoogleAnalytics } from "lib/util/GoogleAnalyticsUtil/logEvent";
import MapRawStringToReactElement from "lib/util/ReactComponentUtil/mapRawStringToReactElement/MapRawStringtoReactElement";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  selectEpicProfile,
  selectPMSettings,
  selectPMSettingsValue,
} from "ui/app/components/myProfile/PatientMaster/ducks/selectors";
import {
  fetchEpicProfile,
  fetchSystemSettings,
} from "ui/app/components/myProfile/PatientMaster/ducks/thunks";
import { useGetInstitutionsByClustersAndGroups } from "ui/appointment/hooks/useGetInstitutionsByClustersAndGroups";
import { useGetTcuList } from "ui/appointment/hooks/useGetTcuList";
import SelectionInstructions from "../../../../common/selection-listing/SelectionInstructions/SelectionInstructions";
import SelectionList from "../../../../common/selection-listing/SelectionList/SelectionList";
import { sxStyles } from "./InstitutionsPage.styles";
import {
  fetchDependentParticulars,
  setProfileAccessEntryPoint,
} from "lib/redux/user";
import {
  selectAppointments,
  selectAppointmentsSystemSettings,
} from "ui/appointment/ducks/selectors";
import InstitutionsPageSkeleton from "./InstitutionsPageSkeleton";
import { Institution } from "api/appointment/GetInstitutionsByClustersAndGroups/getInstitutionsByClustersAndGroups.toUi.types";
import { selectMemberIdentifier, selectUser } from "lib/redux/user/selectors";
import {
  clearAppointmentMetaState,
  setParentInstitutionCode,
  updateTcuRemainingRegInstitutionCodes,
  updateAppointmentMetaFromInstitution,
  updateInstitutionCodeForCreate,
  updateInstitutionForCreate,
  setTcuEmail,
  setFeedbackUrl,
} from "ui/appointment/ducks/appointmentsSlice";
import { selectApptUserPreference } from "lib/redux/apptUserPreference/selectors";
import { setCurrentFlowAction } from "lib/redux/navigation";
import { updateInsUserPreference } from "lib/redux/apptUserPreference";
import { fetchSystemSettings as fetchAppointmentSystemSettings } from "ui/appointment/ducks";
import { PM_ERROR_422 } from "api/patientMaster/GetPatientMasterProfile/getPatientMasterProfile";
import { useGetLiveChatFlag } from "ui/appointment/hooks/useGetLiveChatFlag";

const nextButtonText = "Create now";

const InstitutionsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = sxStyles(theme);
  const dispatch = useAppDispatch();

  // Redux states
  const {
    isLoading: settingsIsLoading,
    hasErrored: settingsHasErrored,
    errorMessage: settingsErrorMessage,
  } = useAppSelector(selectPMSettings);

  const {
    isLoading: isSystemSettingsLoading,
    hasErrored: systemSettingsHasErrored,
    errorMessage: systemSettingsErrorMessage,
    values: appointmentSystemSettingsValues,
  } = useAppSelector(selectAppointmentsSystemSettings);
  const FVInstitutionsList =
    appointmentSystemSettingsValues["Institutions"]?.Value?.split(",") || [];

  const { errorMessages } = useAppSelector(selectPMSettingsValue);
  const {
    hasEpicProfile,
    isLoading: epicIsLoading,
    hasErrored: epicHasErrored,
    errorMessage: epicErrorMessage,
    hasFetchedEpicProfile,
  } = useAppSelector(selectEpicProfile);
  const { isCreateFromAppointmentLanding } =
    useAppSelector(selectAppointments).create;
  const uuid = useAppSelector(selectUser)?.myChartUuid || null;
  const {
    isPatient,
    sources: systemFlowSourceSystems,
    dependentIsLoading: isDependentProfileApiLoading,
    dependentHasErrored: hasDependentProfileApiErrored,
    dependentErrorMessage: dependentProfileApiErrorMessage,
  } = useAppSelector(selectUser);
  const { lastFiveInstitutionSearchedKeywords: lastFiveSearchedKeywords } =
    useAppSelector(selectApptUserPreference);

  // Local states
  const [pageFlag, setPageFlag] = useState(true);
  const [isNoRecordsOpen, setIsNoRecordsOpen] = useState(false);

  const onSelect = (
    institution: string,
    targetSystem: string | null,
    institutionCode: string | null,
  ) => {
    dispatch(updateInstitutionForCreate(institution));
    dispatch(updateInstitutionCodeForCreate(institutionCode));
    dispatch(
      updateAppointmentMetaFromInstitution({
        targetSystem,
        institutionCode,
      }),
    );
  };
  const onSaveInstitutionKeyword = (keyword: string) => {
    dispatch(updateInsUserPreference(keyword));
  };

  const { isLiveChatEnabled, isLiveChatLoading } = useGetLiveChatFlag();
  const [rawInstitutions, isLoading, hasErrored, errorMessage] =
    useGetInstitutionsByClustersAndGroups(systemFlowSourceSystems, pageFlag);

  // For Autogen TCU
  const memberIdentifier = useAppSelector(selectMemberIdentifier);
  const { tcuList, isLoadingTcuList } = useGetTcuList(memberIdentifier ?? "");

  // As of Aug 2021, filter out JCH for form flow
  const institutions = rawInstitutions
    .filter((institution) => institution.institutionCode !== "JCH")
    .map((institution) => ({
      ...institution,
      tcuFlag: tcuList?.some(
        (x) => x.InstitutionCode === institution.institutionCode,
      ),
      tagName: tcuList?.some(
        (x) => x.InstitutionCode === institution.institutionCode,
      )
        ? "Follow-up"
        : null, // Put a tag if the insitution has TCU order
    }));

  const { getInputProps, groupedOptions, inputValue, focused } =
    useAutocomplete({
      options: institutions,
      getOptionLabel: (option) => option.displayName,
    });

  const handleNoRecordsNextClick = () => {
    logEventToGoogleAnalytics(
      EVENTS.SELECT_CREATE_APPOINTMENT_PARTICULARS_CREATE_NOW,
    );

    if (isCreateFromAppointmentLanding) {
      mobileNavigate(
        `${ACTIONS.MOBILE_PM_SYNC}&successUrl=/onenuhs/v2${PATHS.APPOINTMENT_CREATE_INSTITUTIONS.path}`,
      );
    } else {
      mobileNavigate(
        `${ACTIONS.MOBILE_PM_SYNC}&successUrl=/onenuhs/v2${PATHS.APPOINTMENT_CREATE_INSTITUTIONS_MOBILE.path}`,
      );
    }
  };

  const handleNoRecordsCancelClick = () => {
    logEventToGoogleAnalytics(
      EVENTS.SELECT_CREATE_APPOINTMENT_PARTICULARS_OFFLINE_REQUEST,
    );
    setIsNoRecordsOpen(false);
    if (isLiveChatEnabled) {
      mobileNavigate(
        ACTIONS.LIVECHAT_PAGE_INTERCEPTION(PATHS.APPOINTMENT_MOBILE.path),
      );
    }
  };

  useEffect(() => {
    dispatch(clearAppointmentMetaState());
    dispatch(setCurrentFlowAction("APPOINTMENT_CREATE"));
    dispatch(fetchSystemSettings()); //retrieve tnc, disclaimers, error messages etc
    dispatch(
      fetchAppointmentSystemSettings(
        "APPOINTMENT",
        "NUP",
        "NUPSDASlotMessage",
        null,
      ),
    );

    dispatch(
      fetchAppointmentSystemSettings("APPOINTMENT", "FV", "Institutions", null),
    );

    dispatch(setProfileAccessEntryPoint("APPOINTMENT"));

    if (isPatient) {
      dispatch(
        fetchEpicProfile({
          myInfoName: null,
          myInfoDoB: null,
          myInfoSex: null,
          uuid,
          codeVerifier: null,
          callMyInfo: false,
        }),
      );
    } else {
      // For dependents
      dispatch(fetchDependentParticulars());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMemo(() => {
    if (hasFetchedEpicProfile && !hasEpicProfile) {
      setIsNoRecordsOpen(true);
    }
  }, [hasEpicProfile, hasFetchedEpicProfile]);

  return (
    <Page>
      <SelectionInstructions text="Select a NUHS Institution" />
      {isLoading ||
      isLoadingTcuList ||
      isDependentProfileApiLoading ||
      epicIsLoading ||
      settingsIsLoading ||
      isSystemSettingsLoading ||
      isLiveChatLoading ? (
        <InstitutionsPageSkeleton />
      ) : hasErrored ? (
        <ErrorDisplay
          errorMessage={errorMessage}
          onTryAgain={() => {
            setPageFlag(!pageFlag);
          }}
        />
      ) : hasDependentProfileApiErrored ? (
        <ErrorDisplay
          errorMessage={dependentProfileApiErrorMessage}
          onTryAgain={
            dependentProfileApiErrorMessage?.includes(PM_ERROR_422)
              ? undefined
              : () => {
                  dispatch(fetchDependentParticulars());
                }
          }
        />
      ) : settingsHasErrored ? (
        <ErrorDisplay
          errorMessage={settingsErrorMessage}
          onTryAgain={() => {
            dispatch(fetchSystemSettings());
          }}
        />
      ) : systemSettingsHasErrored ? (
        <ErrorDisplay
          errorMessage={systemSettingsErrorMessage}
          onTryAgain={() => {
            dispatch(
              fetchAppointmentSystemSettings(
                "APPOINTMENT",
                "NUP",
                "NUPSDASlotMessage",
                null,
              ),
            );
          }}
        />
      ) : epicHasErrored ? (
        <ErrorDisplay
          errorMessage={epicErrorMessage}
          onTryAgain={
            epicErrorMessage?.includes(PM_ERROR_422)
              ? undefined
              : () => {
                  dispatch(
                    fetchEpicProfile({
                      myInfoName: null,
                      myInfoDoB: null,
                      myInfoSex: null,
                      uuid,
                      codeVerifier: null,
                      callMyInfo: false,
                    }),
                  );
                }
          }
        />
      ) : institutions ? (
        <>
          <Box sx={{ mt: 3, mb: 1, px: 2, zIndex: 100 }}>
            <SimpleAutocomplete
              placeholder="Search institutions"
              showHistoryKeyword={focused && inputValue.length === 0}
              items={lastFiveSearchedKeywords}
              inputProps={getInputProps}
              onSelect={(selectedValue) => {
                const selectedInstitutionObj = institutions.find(
                  (ins) => ins.displayName === selectedValue,
                );

                if (selectedInstitutionObj) {
                  onSelect(
                    selectedValue,
                    selectedInstitutionObj.targetSystem,
                    selectedInstitutionObj.institutionCode,
                  );
                  onSaveInstitutionKeyword(selectedValue);

                  logEventToGoogleAnalytics(
                    EVENTS.VIEW_APPOINTMENT_INSTITUTION,
                  );

                  if (selectedInstitutionObj.parentInstitutionCode) {
                    dispatch(
                      setParentInstitutionCode(
                        selectedInstitutionObj.parentInstitutionCode,
                      ),
                    );
                  }

                  if (!hasEpicProfile && isPatient) {
                    navigate(PATHS.APPOINTMENT_CREATE_FORM.path);
                  } else if (selectedInstitutionObj.tcuFlag) {
                    // !exp this is for TCU(To See You) flow
                    const selectedTcuObject = tcuList?.find(
                      (x) =>
                        x.InstitutionCode ===
                        selectedInstitutionObj.institutionCode,
                    );
                    dispatch(
                      updateTcuRemainingRegInstitutionCodes(
                        selectedTcuObject?.RegInstitutionCode ?? [],
                      ),
                    );
                    dispatch(setTcuEmail(selectedTcuObject?.Email ?? ""));
                    dispatch(
                      setFeedbackUrl(selectedTcuObject?.FeedbackUrl ?? ""),
                    );
                    navigate(PATHS.APPOINTMENT_CREATE_TCU_SERVICES.path);
                  } else if (
                    FVInstitutionsList.includes(
                      selectedInstitutionObj.institutionCode,
                    )
                  ) {
                    // !exp this is for FV(First Visit) flow
                    navigate(PATHS.APPOINTMENT_CREATE_SPECIALTY.path);
                  } else if (!selectedInstitutionObj.webflow) {
                    navigate(PATHS.APPOINTMENT_CREATE_FORM.path);
                  } else {
                    navigate(PATHS.APPOINTMENT_CREATE_SERVICES.path);
                  }
                }
              }}
            />
          </Box>
          {inputValue.length !== 0 && groupedOptions.length === 0 ? (
            <Box sx={classes.notFoundTextBox}>
              <Typography sx={classes.notFoundText}>
                The institution you are looking for is not in this list.
              </Typography>
            </Box>
          ) : (
            <Box sx={classes.selectionListBox}>
              <SelectionList
                items={
                  groupedOptions.length === 0
                    ? institutions
                    : (groupedOptions as Institution[])
                }
                onSelect={(selectedValue) => {
                  const items =
                    groupedOptions.length === 0
                      ? institutions
                      : (groupedOptions as Institution[]);

                  const selectedInstitutionObj = items.find(
                    (ins) => ins.displayName === selectedValue,
                  );

                  if (selectedInstitutionObj) {
                    onSelect(
                      selectedValue,
                      selectedInstitutionObj.targetSystem,
                      selectedInstitutionObj.institutionCode,
                    );
                    onSaveInstitutionKeyword(selectedValue);

                    logEventToGoogleAnalytics(
                      EVENTS.VIEW_APPOINTMENT_INSTITUTION,
                    );

                    if (selectedInstitutionObj.parentInstitutionCode) {
                      dispatch(
                        setParentInstitutionCode(
                          selectedInstitutionObj.parentInstitutionCode,
                        ),
                      );
                    }

                    if (!hasEpicProfile && isPatient) {
                      navigate(PATHS.APPOINTMENT_CREATE_FORM.path);
                    } else if (selectedInstitutionObj.tcuFlag) {
                      const selectedTcuObject = tcuList?.find(
                        (x) =>
                          x.InstitutionCode ===
                          selectedInstitutionObj.institutionCode,
                      );
                      dispatch(
                        updateTcuRemainingRegInstitutionCodes(
                          selectedTcuObject?.RegInstitutionCode ?? [],
                        ),
                      );
                      dispatch(setTcuEmail(selectedTcuObject?.Email ?? ""));
                      dispatch(
                        setFeedbackUrl(selectedTcuObject?.FeedbackUrl ?? ""),
                      );
                      navigate(PATHS.APPOINTMENT_CREATE_TCU_SERVICES.path);
                    } else if (
                      FVInstitutionsList.includes(
                        selectedInstitutionObj.institutionCode,
                      )
                    ) {
                      // !exp FV flow
                      navigate(PATHS.APPOINTMENT_CREATE_SPECIALTY.path);
                    } else if (!selectedInstitutionObj.webflow) {
                      navigate(PATHS.APPOINTMENT_CREATE_FORM.path);
                    }
                    // TODO: To add here the popup and logic for redirecting
                    else {
                      navigate(PATHS.APPOINTMENT_CREATE_SERVICES.path);
                    }
                  }
                }}
              />
            </Box>
          )}
          <ConfirmationModal
            title={errorMessages.noRecordsFound.title}
            body={MapRawStringToReactElement(
              errorMessages.noRecordsFound.description ?? "",
            )}
            nextButtonText={nextButtonText} //"Create now"
            disableResetOnNext={true}
            onClickNext={handleNoRecordsNextClick}
            cancelButtonText={
              isLiveChatEnabled ? "Chat with us" : "Make offline request"
            }
            onClickCancel={handleNoRecordsCancelClick}
            open={isNoRecordsOpen}
            onClose={() => {}}
            showCloseButton={false}
            hideCancelButton={false}
          />
        </>
      ) : null}
    </Page>
  );
};

export default InstitutionsPage;
